/**
 * Shared utility for checking whether an asset distribution is fully assigned.
 *
 * Works with both Frame246 (BeneficiaryAllocation) and Frame247 (BeneficiaryInfo)
 * types since it only requires an array of objects with a `percentage` field.
 */

interface HasPercentage {
  percentage?: number | string;
}

interface HasPrimaryBeneficiaries {
  primaryBeneficiaries?: HasPercentage[] | null;
}

/**
 * Returns true only if the distribution has at least one primary beneficiary
 * AND the sum of all primary beneficiary percentages equals exactly 100.
 */
export function isFullyAssigned(distribution: HasPrimaryBeneficiaries | null | undefined): boolean {
  if (!distribution?.primaryBeneficiaries?.length) return false;
  const total = distribution.primaryBeneficiaries.reduce(
    (sum, b) => sum + (Number(b.percentage) || 0),
    0
  );
  return total === 100;
}

// ---------------------------------------------------------------------------
// Cascade-cleanup helpers for beneficiary deletion across inventory frames.
// These mirror the backend logic in server/services/unifiedContactService.js
// (deleteContact cascade) but operate on frontend session data at write time.
// ---------------------------------------------------------------------------

interface SourceBeneficiary {
  contactId: string;
}

export interface DistributionAllocation {
  beneficiaryId: string;
  percentage?: number | string;
}

export interface DistributionRecord {
  assetId?: string;
  primaryBeneficiaries?: DistributionAllocation[];
  contingentBeneficiaries?: DistributionAllocation[];
  [key: string]: unknown;
}

/** Convenience alias for the distributions map stored in session */
export type DistributionMap = Record<string, DistributionRecord>;

/**
 * Diffs old vs new beneficiary arrays and returns the contactIds that were
 * removed (present in old but absent in new).
 */
export function computeRemovedBeneficiaryIds(
  oldBeneficiaries: SourceBeneficiary[] | undefined,
  newBeneficiaries: SourceBeneficiary[] | undefined
): string[] {
  const oldIds = new Set((oldBeneficiaries ?? []).map((b) => b.contactId).filter(Boolean));
  const newIds = new Set((newBeneficiaries ?? []).map((b) => b.contactId).filter(Boolean));
  return [...oldIds].filter((id) => !newIds.has(id));
}

/**
 * Returns a new distributions map with the given contactIds removed from the
 * primaryBeneficiaries and contingentBeneficiaries of the specified asset's
 * record. Handles both Record<assetId, DistributionRecord> format (current)
 * and gracefully no-ops when the asset has no distribution record.
 */
export function cleanDistributionsForAsset(
  distributions: Record<string, DistributionRecord>,
  assetId: string,
  removedContactIds: string[]
): Record<string, DistributionRecord> {
  if (!distributions[assetId] || removedContactIds.length === 0) {
    return distributions;
  }
  const removedSet = new Set(removedContactIds);
  const record = distributions[assetId];
  return {
    ...distributions,
    [assetId]: {
      ...record,
      primaryBeneficiaries: (record.primaryBeneficiaries ?? []).filter(
        (b) => !removedSet.has(b.beneficiaryId)
      ),
      contingentBeneficiaries: (record.contingentBeneficiaries ?? []).filter(
        (b) => !removedSet.has(b.beneficiaryId)
      ),
    },
  };
}

/**
 * Returns a new distributions map with the entire record for the given assetId
 * removed. Used when an asset is deleted entirely.
 */
export function removeAssetDistribution(
  distributions: Record<string, DistributionRecord>,
  assetId: string
): Record<string, DistributionRecord> {
  if (!distributions[assetId]) return distributions;
  const { [assetId]: _removed, ...rest } = distributions;
  return rest;
}

/**
 * Auto-syncs the distribution record for an asset when its beneficiary list
 * changes in an inventory modal. Always recalculates primaryBeneficiaries to
 * equal-split percentages summing to exactly 100.
 *
 * - 0 valid beneficiaries → removes the distribution record entirely
 * - 1+ valid beneficiaries → sets primaryBeneficiaries to equal-split,
 *   preserving contingentBeneficiaries, instructions, notes, and all other
 *   metadata from any existing record.
 *
 * Beneficiaries with an empty/missing contactId are filtered out before
 * calculating the split.
 */
export function autoSyncDistributionForAsset(
  distributions: Record<string, DistributionRecord>,
  assetId: string,
  beneficiaries: { contactId: string }[]
): Record<string, DistributionRecord> {
  const validBeneficiaries = beneficiaries.filter((b) => !!b.contactId);

  if (validBeneficiaries.length === 0) {
    return removeAssetDistribution(distributions, assetId);
  }

  const n = validBeneficiaries.length;
  const base = Math.floor(100 / n);
  const extra = 100 % n;

  const primaryBeneficiaries: DistributionAllocation[] = validBeneficiaries.map((b, i) => ({
    beneficiaryId: b.contactId,
    percentage: i < extra ? base + 1 : base,
  }));

  const existing = distributions[assetId] ?? {};
  return {
    ...distributions,
    [assetId]: {
      ...existing,
      assetId,
      primaryBeneficiaries,
    },
  };
}
